import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, inject } from "@angular/core";
import { Logger } from "@dtm-frontend/shared/utils";
import { IMessage, RxStompState } from "@stomp/rx-stomp";
import { Observable, combineLatest, merge, pipe, share } from "rxjs";
import { filter, map } from "rxjs/operators";
import { RX_STOMP_CONNECTIONS, RxStompConnection } from "../shared-websocket.module";
import { WebsocketConnectionStatus, WebsocketMessage } from "./websocket.models";

@Injectable()
export class MultiWebsocketService {
    private readonly document = inject(DOCUMENT);
    private readonly sharedWatchers: Map<string, Observable<WebsocketMessage>> = new Map();

    public readonly connectionStatuses$ = combineLatest(
        this.rxStompConnections.map((connection) =>
            connection.service.connectionState$.pipe(
                map((state) => ({
                    endpoint: connection.endpoint,
                    status: this.mapConnectionStatus(state),
                }))
            )
        )
    ).pipe(
        map((statuses) =>
            statuses.reduce<Record<string, WebsocketConnectionStatus>>((accumulator, status) => {
                accumulator[status.endpoint] = status.status;

                return accumulator;
            }, {})
        )
    );

    public readonly connectionStatus$ = this.connectionStatuses$.pipe(
        map((statuses) => {
            const relevantStatuses = this.rxStompConnections
                .filter((connection) => !connection.lazyConnect || connection.service.active)
                .map((connection) => statuses[connection.endpoint])
                .filter((status): status is WebsocketConnectionStatus => status !== undefined);

            return this.mapAggregateConnectionStatus(relevantStatuses);
        })
    );

    constructor(
        @Inject(RX_STOMP_CONNECTIONS)
        private readonly rxStompConnections: RxStompConnection[]
    ) {
        this.rxStompConnections.forEach((connection) => {
            connection.service.webSocketErrors$.subscribe((error) => {
                Logger.captureMessage("WebSocket error", {
                    extra: {
                        endpoint: connection.endpoint,
                        error,
                    },
                    level: "error",
                });
            });
        });

        this.document.defaultView?.addEventListener("offline", async () => {
            const previouslyActive = this.rxStompConnections.filter((connection) => connection.service.active);
            await Promise.all(previouslyActive.map((connection) => connection.service.deactivate()));
            previouslyActive.forEach((connection) => connection.service.activate());
        });
    }

    private mapConnectionStatus(state: RxStompState): WebsocketConnectionStatus {
        switch (state) {
            case RxStompState.OPEN:
                return WebsocketConnectionStatus.Connected;
            case RxStompState.CLOSED:
                return WebsocketConnectionStatus.Disconnected;
            case RxStompState.CONNECTING:
                return WebsocketConnectionStatus.Connecting;
            case RxStompState.CLOSING:
                return WebsocketConnectionStatus.Disconnecting;
        }
    }

    public watchTopic(topicName: string, eventTypes?: string[], endpoint?: string): Observable<WebsocketMessage> {
        const cacheKey = this.getWatcherCacheKey(topicName, endpoint);
        const sharedWatcher = this.sharedWatchers.get(cacheKey);

        if (sharedWatcher) {
            return sharedWatcher.pipe(this.filterByEvents(eventTypes));
        }

        const connections = this.getConnections(endpoint);
        connections.forEach((connection) => {
            if (!connection.service.active) {
                connection.service.activate();
            }
        });

        const watcher = merge(...connections.map((connection) => connection.service.watch(topicName))).pipe(share());

        this.sharedWatchers.set(cacheKey, watcher);

        return watcher.pipe(this.filterByEvents(eventTypes));
    }

    private getConnections(endpoint?: string): RxStompConnection[] {
        if (!endpoint) {
            return this.rxStompConnections.filter((connection) => !connection.lazyConnect);
        }

        const connections = this.rxStompConnections.filter((connection) => connection.endpoint === endpoint);

        if (connections.length === 0) {
            throw new Error(`WebSocket endpoint is not configured: ${endpoint}`);
        }

        return connections;
    }

    private getWatcherCacheKey(topicName: string, endpoint?: string): string {
        return `${endpoint ?? "*"}:${topicName}`;
    }

    private mapAggregateConnectionStatus(statuses: WebsocketConnectionStatus[]): WebsocketConnectionStatus {
        if (statuses.length === 0) {
            return WebsocketConnectionStatus.Disconnected;
        }

        if (statuses.every((status) => status === WebsocketConnectionStatus.Connected)) {
            return WebsocketConnectionStatus.Connected;
        }

        if (statuses.some((status) => status === WebsocketConnectionStatus.Connecting)) {
            return WebsocketConnectionStatus.Connecting;
        }

        if (statuses.some((status) => status === WebsocketConnectionStatus.Disconnecting)) {
            return WebsocketConnectionStatus.Disconnecting;
        }

        return WebsocketConnectionStatus.Disconnected;
    }

    private filterByEvents(eventTypes?: string[]) {
        return pipe(
            filter((message: IMessage) => {
                if (!eventTypes) {
                    return true;
                }

                return eventTypes.includes(message.headers["event-type"]);
            })
        );
    }
}
